<template>
  <div
    id="info-box-wrapper"
    ref="infoBoxWrapper" >
    <div class="relative">
      <div
        id="tooltip-assign-wrapper"
        class="infobox-wrapper"
        @click="toggleMenu">
        <div
          id="tooltip-assign"
          class="assigned-member-info">
          <SBAvatarImage
            class="avatar no-shrink"
            :name="isUnassigned || !assignedToMemberData ? '?' : assignedToMemberData.name"
            :image="isUnassigned || !assignedToMemberData ? '' : assignedToMemberData.avatar"
            :available="isUnassigned || !assignedToMemberData ? false : assignedToMemberData.available && assignedToMemberData.online"
            :show-availability-status="false"
            :size="SBAvatarImageSize.W16"/>
          <span class="ellipsis">{{ isUnassigned || !assignedToMemberData ? 'Assign' : assignedToMemberData.name }}</span>
          <span
            class="arrow no-shrink"
            :class="{active: menuOpen}"/>
        </div>

        <!-- Popovers -->
        <b-popover
          v-if="!menuOpen"
          target="tooltip-assign-wrapper"
          triggers="hover"
          placement="bottom"
          custom-class="basic-popover" >
          Assign conversation
        </b-popover>

        <b-popover
          ref="assignPopover"
          target="tooltip-assign-wrapper"
          triggers="manual"
          container="info-box-wrapper"
          :boundary="$refs.infoBoxWrapper"
          :boundary-padding="-9"
          placement="bottomright"
          fallback-placement="flip"
          custom-class="interactive-popover hide-arrow " >
          <AssignConversationAssignMenu
            v-click-outside="closeMenu"
            class="menu"
            @click.stop
            @click.native.stop
            @item-selected="assignConversationToMember"/>
        </b-popover>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ClickOutside from 'v-click-outside';
import AssignConversationAssignMenu from './AssignMenu';
import SBAvatarImage from '../../common/AvatarImage';
import SBAvatarImageSize from '../../../helpers/lists/avatarSize';
import randomString from '@/helpers/randomString';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import ToasterMixin from '@/mixins/Toaster';

export default {
  name: 'AssignConversationInfoBox',
  directives: {
    ClickOutside: ClickOutside.directive,
  },
  components: {
    SBAvatarImage,
    AssignConversationAssignMenu,
  },
  mixins: [
    UserPermissionsMixin,
    ToasterMixin,
  ],
  data() {
    return {
      menuOpen: false,
      SBAvatarImageSize,
    };
  },
  computed: {
    ...mapGetters({
      activeConversation: 'chat/activeConversation',
      getMemberById: 'organisation/getMemberById',
      getBusinessById: 'organisation/getBusinessById',
      currentOrganisation: 'organisation/currentOrganisation',
      me: 'organisation/me',
    }),
    isUnassigned() {
      // console.log('conversation info', this.activeConversation);
      return !(this.activeConversation && this.activeConversation.user_id);
    },
    assignedToMemberData() {
      if (this.activeConversation && this.activeConversation.user_id) {
        const targetMember = this.getMemberById(this.activeConversation.user_id);
        if (targetMember && targetMember.name) {
          return targetMember;
        }
      }
      return {
        name: 'Assign',
        avatar: '',
        online: false,
        available: false,
      };
    },
  },
  mounted() {
    this.toggleMenu(); // Just to initialize menu position
    this.closeMenu();
  },
  methods: {
    ...mapActions({
      reassignConversationOrRevert: 'chat/reassignConversationOrRevert',
    }),
    toggleMenu() {
      if (this.checkPermission('assign_conversations')) {
        this.menuOpen = !this.menuOpen;
        if (this.menuOpen) {
          this.$refs.assignPopover.$emit('open');
        } else {
          this.closeMenu();
        }
      } else {
        this.showToaster('You don’t have a permission to do this action.', null, null, 'warning');
      }
    },
    closeMenu() {
      this.menuOpen = false;
      if (this.$refs.assignPopover) {
        this.$refs.assignPopover.$emit('close');
      }
    },
    assignConversationToMember(memberData) {
      const memberId = memberData.id;

      if (this.activeConversation && (this.activeConversation.user_id === memberId)) {
        this.closeMenu();
        return; // Prevent closing if the same is selected;
      }

      // send reassign data to backend
      const payload = {
        outboundData: {
          organisationId: this.currentOrganisation.id,
          businessId: this.activeConversation.business_id,
          conversationId: this.activeConversation.id,
          userId: memberId,
          revertUserId: this.activeConversation.user_id,
          timestamp: this.$moment().unix(),
          action: 'assignment',
          direction: 'activity',
          from_user_id: this.me.id,
          temp_sid: randomString(),
          to_user_id: memberId,
        },
        conversationId: this.activeConversation.id,
      };
      this.reassignConversationOrRevert(payload);

      // Check if in My Chats, unassigning (or assigning to someone else), and not following
      const isInMyChats = ['OrganisationConversation', 'OrganisationWithId'].includes(this.$route.name);
      const isUnassigningFromSelf = memberId !== this.me.id;
      const isNotFollowing = !this.activeConversation.following;

      if (isInMyChats && isUnassigningFromSelf && isNotFollowing) {
        // Emit event to remove conversation from My Chats list
        this.$eventBus.$emit('unassign-conversation', this.activeConversation.id);
      }

      // close the menu
      this.closeMenu();
    },
  },
};
</script>

<style lang="scss" scoped>
#info-box-wrapper {
  flex-shrink: 1;
}
.infobox-wrapper {
  position: relative;
  border: 1px solid $greyBorder;
  padding: 0;
  border-radius: 20px;
  box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.1);

  &:hover {
    box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.3);
  }
}

.assigned-member-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #141c20;
  padding: 6px 9px 6px 6px;

  .avatar {
    margin-right: 6px;
  }

  &:hover {
    border-radius: 50px;
    // box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.3);
    .arrow {
      background-image: url('~@/assets/img/SB-Icons/down-arrow-dark.svg');
    }
  }
}

.arrow {
  margin-left: 6px;
  height: 16px;
  width: 12px;
  display: inline-block;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url('~@/assets/img/SB-Icons/down-arrow-light.svg');
}

.arrow.active {
  background-image: url('~@/assets/img/SB-Icons/down-arrow-dark.svg');
}

.menu {
  z-index: 100;
  width: 297px;
  background-color: $white;
  border-radius: 5px;
  box-shadow: 0 2px 5px 0 rgba(13, 15, 22, 0.23)
}

.interactive-popover {
  margin-top: 42px;
}
</style>
